import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { LuLeaf, LuRefreshCw } from 'react-icons/lu'
import { shipmentsService } from '../../../../api/services/shipments.service'
import type { LiveCarbonFootprint } from '../../../../api/models/response/sustainability'
import { themeColors } from '../../../../theme'

// Estados (pre peso-neto) en los que la huella aún es parcial y vale la pena refrescar en vivo.
// 2 EnTransito · 3 Desviado · 4 EnRecepcion · 11 Muestreado · 12 EnConciliacion · 13 Conciliado
const LIVE_STATES = new Set([2, 3, 4, 11, 12, 13])
const POLL_MS = 15000

function fmt(v: number | null | undefined, d = 2): string {
  if (v == null) return '—'
  return v.toLocaleString('es-PE', { maximumFractionDigits: d })
}

export default function CarbonFootprintPanel({
  shipmentId,
  shipmentState,
}: {
  shipmentId: string
  shipmentState: number
}) {
  const [data, setData] = useState<LiveCarbonFootprint | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchData = useCallback(async () => {
    try {
      const res = await shipmentsService.carbonLive(shipmentId)
      setData(res)
      setError(false)
      // Una vez sellado el definitivo, dejamos de refrescar.
      if (res.isFinal && timer.current) {
        clearInterval(timer.current)
        timer.current = null
      }
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [shipmentId])

  useEffect(() => {
    void fetchData()
    // Auto-refresco solo mientras el traslado está activo (pre peso-neto).
    if (LIVE_STATES.has(shipmentState)) {
      timer.current = setInterval(() => void fetchData(), POLL_MS)
    }
    return () => {
      if (timer.current) clearInterval(timer.current)
      timer.current = null
    }
  }, [fetchData, shipmentState])

  const c = themeColors
  const isLive = !!data && !data.isFinal
  const accent = isLive ? '#a8740e' : '#b4532a'

  return (
    <Box border={`1px solid ${c.border}`} borderRadius="2xl" p={5} bg={c.bgCard}>
      <Flex alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Flex alignItems="center" gap={2}>
          <LuLeaf size={18} color="#b4532a" />
          <Text fontSize="md" fontWeight="700" color={c.text}>Huella de Carbono</Text>
          {data && (
            <Box
              px={2}
              py="2px"
              borderRadius="full"
              bg={isLive ? '#f9eccf' : '#dcefed'}
              color={isLive ? '#92400e' : '#1d6664'}
              fontSize="10px"
              fontWeight="700"
              letterSpacing="0.04em"
            >
              {isLive ? '● EN VIVO' : 'DEFINITIVO'}
            </Box>
          )}
        </Flex>
        <Box
          as="button"
          display="inline-flex"
          alignItems="center"
          gap={1}
          fontSize="xs"
          color={c.textMuted}
          cursor="pointer"
          _hover={{ color: c.text }}
          onClick={() => void fetchData()}
        >
          <LuRefreshCw size={13} /> Actualizar
        </Box>
      </Flex>

      {loading && !data && (
        <Text fontSize="sm" color={c.textMuted}>Calculando…</Text>
      )}

      {error && !data && (
        <Text fontSize="sm" color="#9a3020">No se pudo calcular la huella.</Text>
      )}

      {data && (
        <>
          <Flex alignItems="baseline" gap={2} mb={1}>
            <Text fontSize="3xl" fontWeight="800" color={accent} lineHeight="1">
              {fmt(data.co2eTons, 4)}
            </Text>
            <Text fontSize="sm" color={c.textMuted} fontWeight="600">tCO2e</Text>
            <Text fontSize="xs" color={c.textDimmed}>({fmt(data.co2eKg, 1)} kg)</Text>
          </Flex>

          {!data.isComplete && data.incompleteReason ? (
            <Text fontSize="xs" color={c.textMuted} mb={2}>{data.incompleteReason}</Text>
          ) : (
            <Flex gap={6} flexWrap="wrap" mt={3}>
              <Metric
                label="Distancia"
                value={`${fmt(data.distanceKm, 1)} km`}
                hint={data.distanceSource === 'Gps' ? 'GPS real' : data.distanceSource === 'UtmEstimate' ? 'estimada' : undefined}
              />
              <Metric label="Combustible" value={`${fmt(data.fuelGallons, 1)} gal`} hint={data.fuelType ?? undefined} />
              <Metric
                label="Intensidad"
                value={data.intensityKgPerTon != null ? `${fmt(data.intensityKgPerTon, 1)} kg/TM` : '—'}
                hint={data.weightBasis === 'Declared' ? 'peso declarado (estim.)' : data.weightBasis === 'Net' ? 'peso oficial' : undefined}
              />
            </Flex>
          )}

          <Text fontSize="10px" color={c.textDimmed} mt={3}>
            {isLive
              ? `Valor parcial desde ${data.gpsPointCount} puntos GPS; se actualiza durante el viaje y se sella al cerrar el peso neto.`
              : 'Valor definitivo sellado tras el pesaje neto.'}
          </Text>
        </>
      )}
    </Box>
  )
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const c = themeColors
  return (
    <Flex direction="column" gap="2px">
      <Text fontSize="10px" color={c.textMuted} fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      <Text fontSize="sm" color={c.text} fontWeight="700">{value}</Text>
      {hint && <Text fontSize="10px" color={c.textDimmed}>{hint}</Text>}
    </Flex>
  )
}
